import React, { useState } from "react";
import {
  FaThumbsUp,
  FaRegThumbsUp,
  FaThumbsDown,
  FaRegThumbsDown,
} from "react-icons/fa";
import SubmitFeedbackModal from "../../modals/SubmitFeedbackModal";

/**
 * VoteBox Component
 *
 * This component allows users to rate suggestions with a thumbs up or thumbs down.
 *
 * Props:
 * @param {string} itemId - The unique identifier for the item being rated.
 * @param {string|null} feedbackType - The type of feedback ('positive' or 'negative') to display the appropriate icon.
 * @param {function} onRate - The function to be called when the user rates the suggestion. this function will receive 4 parameters:
 * (feedbackType, itemId, feedbackMessage, ...additionalData)
 * @param {array} additionalData - An optional array of additional data to be passed to the onRate function.
 * @param {string} itemType - The type of item being rated ('conference', 'journal', 'insight')
 * @param {function} onSuccess - Optional callback for when feedback is submitted
 *
 * @returns {JSX.Element} - The VoteBox component.
 * @example
 * <VoteBox
 *   itemId="123"
 *   feedbackType="positive"
 *   onRate={(feedbackType, itemId, feedbackMessage, ...additionalData) => console.log(feedbackType, itemId, feedbackMessage, ...additionalData)}
 *   itemType="conference"
 *   onSuccess={() => console.log("Feedback submitted")}
 * />
 */

const VoteBox = ({ 
  itemId, 
  feedbackType, // positive or negative
  onRate, 
  additionalData = [], 
  itemType = "insight",
  onSuccess 
}) => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  return (
    <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
      <span className="fw-normal me-2">Rate the suggestion</span>
      <button
        className="btn btn-sm"
        onClick={()=>{
          if (feedbackType === "positive") {
            onRate(null, itemId, null,...additionalData);
          } else {
            onRate("positive", itemId, null, ...additionalData);
          }
        }}
      >
        {feedbackType === "positive" ? (
          <FaThumbsUp size={18} color="green" />
        ) : (
          <FaRegThumbsUp size={18} color="green" />
        )}
      </button>
      <button
        className="btn btn-sm"
        onClick={()=>{
          if (feedbackType === "negative") {
            onRate(null, itemId, null, ...additionalData);
          } else {
            setShowFeedbackModal(true);
          }
        }}
      >
        {feedbackType === "negative" ? (
          <FaThumbsDown
            size={18}
            color="red"
            style={{ transform: "scaleX(-1)" }}
          />
        ) : (
          <FaRegThumbsDown
            size={18}
            color="red"
            style={{ transform: "scaleX(-1)" }}
          />
        )}
      </button>
      <SubmitFeedbackModal
          show={showFeedbackModal}
          handleClose={() => setShowFeedbackModal(false)}
          itemId={itemId}
          itemType={itemType}
          onSuccess={(feedbackMessage) => {
            if (onSuccess) onSuccess(itemId, feedbackMessage);
            onRate("negative", itemId, feedbackMessage, ...additionalData);
          }}
          vote={feedbackType === "positive" ? true : false}
          additionalData={additionalData}
        />
    </div>
  );
};

export default VoteBox;
